import { ImageRetry } from '@openeducation/oe-rn-components';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import styles from './four-square-activity.style';
import { SizeBreakpoint } from '../../../../enums/size-breakpoint';
import { TemplateType } from '../../../../enums/template-type';
import { Fonts } from '../../../../fonts';
import { useColors } from '../../../../hooks';
import { useActivityContext } from '../../../../hooks/use-activity.context';
import { useErrorSound } from '../../../../hooks/use-error-sound';
import { useSuccessSound } from '../../../../hooks/use-success-sound';
import useVibration from '../../../../hooks/use-vibration';
import { arrayShuffle } from '../../../../utils/array';
import KeyboardLegend, {
  KeyboardLegendPadding,
} from '../../../keyboard-legend/keyboard-legend';
import LessonAccentBanner from '../../../lesson-accent-banner/lesson-accent-banner';
import LessonInstructions from '../../../lesson-instructions/lesson-instructions';
import LessonPlayerFrame from '../../../lesson-player-frame/lesson-player-frame';
import { breakpoint, isBigScreen } from '../../../media-query';
import ThemedText2 from '../../../themed-text-2';
import TouchableCard from '../../../touchable-card/touchable-card';

interface IncorrectImageInterface {
  incorrectImage: string;
}

interface FourSquareActivityInterface {
  type: '4SQ' | '4SQ - EXTENDED';
  instructions: string;
  instructionsTranslation?: string;
  word: string;
  correctImage: string;
  steps: IncorrectImageInterface[];
  correctFeedback: string;
  correctFeedbackTranslation?: string;
  incorrectFeedback: string;
  incorrectFeedbackTranslation?: string;
}

interface Option {
  url: string;
  isCorrect: boolean;
}

export function FourSquareActivity() {
  const colors = useColors();
  const { t } = useTranslation();
  const {
    currentActivity,
    completeCurrentActivity,
    isCurrentActivityComplete,
  } = useActivityContext();
  const correctSound = useSuccessSound();
  const incorrectSound = useErrorSound();
  const { vibrate } = useVibration();

  const activity = currentActivity as FourSquareActivityInterface;
  const [complete, setComplete] = useState(false);
  const [selectedOption, setSelectedOption] = useState<number | undefined>(
    undefined
  );
  const [showResult, setShowResult] = useState(false);

  const options: Option[] = useMemo(
    () =>
      arrayShuffle([
        { url: activity.correctImage, isCorrect: true },
        ...activity.steps.map((step) => ({
          url: step.incorrectImage,
          isCorrect: false,
        })),
      ]),
    [activity]
  );

  const wordFontWeight = useMemo(() => {
    if (isBigScreen()) {
      return Fonts.primary.bold;
    }
    return activity.word.split(' ').length < 4
      ? Fonts.primary.bold
      : Fonts.primary.regular;
  }, [activity.word]);

  useEffect(() => {
    setShowResult(false);
  }, [selectedOption]);

  useEffect(() => {
    if (!showResult || selectedOption === undefined || complete) {
      return;
    }

    const timeout = setTimeout(() => {
      setSelectedOption(undefined);
    }, 2000);

    return () => {
      clearTimeout(timeout);
    };
  }, [showResult, selectedOption, complete]);

  const selectedColor = useMemo(() => {
    if (selectedOption === undefined) {
      return colors.borderColor;
    }

    if (!showResult) {
      return colors.primary;
    }

    const option = options[selectedOption];
    if (showResult && option.isCorrect) {
      return colors.success;
    }

    return colors.error;
  }, [selectedOption, showResult, options, colors]);

  const answerActivity = () => {
    if (selectedOption === undefined) {
      return;
    }

    const option = options[selectedOption];

    if (option.isCorrect) {
      completeCurrentActivity();
      setComplete(true);
      correctSound.play();
    } else {
      incorrectSound.play();
      vibrate();
    }

    setShowResult(true);
  };

  const optionsInSetsOfTwo = options.reduce(
    (result: any[], _currentOption, index) => {
      return index % 2 === 0
        ? result.concat([options.slice(index, index + 2)])
        : result;
    },
    []
  );

  const indexFromSet = (
    setIndex: number,
    index: number,
    amountInSet = 2
  ): number => setIndex * amountInSet + index;

  return (
    <>
      <LessonPlayerFrame
        showCheckButton={selectedOption !== undefined && !complete}
        onCheckButtonPress={answerActivity}
        showContinueButton={complete}
      >
        <LessonAccentBanner
          style={styles.accentBanner}
          innerContainerStyle={styles.accentBannerInnerContainer}
        >
          <LessonInstructions
            translation={currentActivity.instructionsTranslation}
          >
            {currentActivity.instructions}
          </LessonInstructions>
          <ThemedText2 type="onPrimary" style={[styles.word, wordFontWeight]}>
            {activity.word}
          </ThemedText2>
        </LessonAccentBanner>
        <View style={styles.imagesContainer}>
          {optionsInSetsOfTwo.map((set, setIndex) => (
            <View style={styles.imageSet} key={setIndex}>
              {set.map(({ url }, index) => (
                <TouchableCard
                  containerStyle={styles.cardWrapper}
                  key={url + indexFromSet(setIndex, index)}
                  keyCode={(indexFromSet(setIndex, index) + 1).toString()}
                  onPress={() => {
                    setSelectedOption(indexFromSet(setIndex, index));
                  }}
                  selected={selectedOption === indexFromSet(setIndex, index)}
                  selectedColor={selectedColor}
                  disabled={complete}
                >
                  <ImageRetry
                    style={[
                      styles.image,
                      // eslint-disable-next-line react-native/no-inline-styles
                      {
                        width: breakpoint(SizeBreakpoint.MD) ? 240 : 150,
                      },
                    ]}
                    accessibilityIgnoresInvertColors
                    source={{ uri: url }}
                  />
                </TouchableCard>
              ))}
            </View>
          ))}
        </View>
        {selectedOption === undefined && <KeyboardLegendPadding />}
      </LessonPlayerFrame>
      <KeyboardLegend
        templateType={TemplateType.FOURSQ}
        forceHide={!!selectedOption || isCurrentActivityComplete}
        text={t('keyboardLegend.answer')}
        indicatorList={options.map((_, i) => (i + 1).toString())}
      />
    </>
  );
}
